import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Tabs } from 'expo-router';
import { LabelText, MainTabBarV2, theme, useResponsive } from '@sg/garnish';

import { Navbar } from '../../components';
import { APP_BACKGROUND } from '../constants';
import { useTabOptions } from '../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export default function TabsLayout() {
  const { currentBreakpoint, minWidth } = useResponsive();

  const {
    homeTabOptions,
    menuTabOptions,
    giftTabOptions,
    loyaltyTabOptions,
    reorderTabOptions,
  } = useTabOptions();

  // ─────────────────────────────────────────────────────────────────────

  const screenOptions = useMemo<TabsScreenOptions>(
    () => ({
      headerShown: !currentBreakpoint.isXS,
      header: currentBreakpoint.isXS ? undefined : renderNavbar,
      sceneContainerStyle: { backgroundColor: APP_BACKGROUND },
      tabBarStyle: {
        backgroundColor: APP_BACKGROUND,
        borderTopWidth: 1,
        borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
        paddingTop: theme.spacing[2],
      },
      tabBarActiveTintColor: theme.colors.KALE,
      tabBarInactiveTintColor: theme.colors.OPACITY.BLACK.DARK,
    }),
    [currentBreakpoint.isXS],
  );

  // ─── Tab Bar ────────────────────────────────────────────────────────

  const renderTabBar = useCallback<TabBarRenderer>(
    (props) => {
      if (minWidth.isSM) return null;

      return (
        <View>
          <View pointerEvents="none" style={styles.expoRouterBadge}>
            <LabelText sizeMatch={['12']}>Expo Router</LabelText>
          </View>

          <MainTabBarV2 {...props} />
        </View>
      );
    },
    [minWidth.isSM],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Tabs
      screenOptions={screenOptions}
      tabBar={renderTabBar}
      backBehavior={Platform.select({ web: 'history', default: 'firstRoute' })}
    >
      <Tabs.Screen name="(home)" options={homeTabOptions} />
      <Tabs.Screen name="(menu)" options={menuTabOptions} />
      <Tabs.Screen name="sg-rewards" options={loyaltyTabOptions} />
      <Tabs.Screen name="reorder-tab" options={reorderTabOptions} />
      <Tabs.Screen name="gift" options={giftTabOptions} />

      {/* hide some tabs */}

      <Tabs.Screen name="account" options={{ href: null }} />
      <Tabs.Screen name="menu" options={{ href: null }} />
    </Tabs>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderNavbar() {
  return <Navbar />;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  expoRouterBadge: {
    position: 'absolute',
    top: 0,
    left: '50%',
    zIndex: 1,
    transform: [{ translateX: '-50%' }, { translateY: '-100%' }],
    backgroundColor: theme.colors.LIME,
    borderTopLeftRadius: theme.spacing['2'],
    borderTopRightRadius: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    ...theme.elevations['3'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type TabsScreenOptions = NonNullable<
  ComponentProps<typeof Tabs>['screenOptions']
>;

type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
